import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';

// Local imports
import { EventBroker } from 'BMapsSrc/eventbroker';
import {
    fetchJobs, loadResult, updateJobErrors, removeJob,
} from 'BMapsSrc/redux/jobs/thunks';
import { useDispatch, useSelector } from 'BMapsRedux';
import { jobDefinitions, JobStatus } from 'BMapsSrc/jobs/JobDefinition';
import { LoadingScreen } from 'BMapsSrc/LoadingScreen';
import { okToClearAndLoad } from 'BMapsSrc/project_data';
import { App } from 'BMapsSrc/BMapsApp';
import BMapsTooltip from 'BMapsSrc/ui/BMapsTooltip';
import { Loader } from 'BMapsSrc/Loader';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs';
import DialogContentSection from 'BMapsSrc/ui/common/DialogContentSection';
import { getJobState } from 'BMapsSrc/redux/jobs/access';
import LoadingIconButton from 'BMapsUI/common/LoadingIconButton';
import { DialogCancelButton, DialogPrimaryButton } from 'BMapsSrc/ui/common/DialogActionButtons';
import { wrapForDisabledTooltip } from 'BMapsSrc/ui/common/helpers';
import { ImportErrors } from '../../import_components';

export default function JobsStructuresTab({
    initData, closeImportPane, selected, selectivityProps, connection,
}) {
    // Todo: JohsStructureTab should only show jobs for types with loadsMacromoleculeStructure
    const { jobs, status, localJobState } = useSelector((state) => state.jobs);
    const dispatch = useDispatch();
    const retrieveJobs = () => dispatch(fetchJobs());
    const highlightedJob = initData?.jobid;
    const [flashId, setFlashId] = useState(null);
    const [errors, setErrors] = useState([]);
    const structureJobs = useMemo(
        () => jobs.filter((job) => jobDefinitions[job.jobtype]?.loadsMacromoleculeStructure), [jobs]
    );

    const loadJobsList = async () => {
        const { error } = await retrieveJobs();
        if (error) {
            setErrors([error]);
        } else {
            setErrors([]);
        }
    };

    useEffect(() => {
        if (status === 'unloaded') {
            loadJobsList();
        }
    }, [status]);

    // Animates the flash when a new job is created, so that it can be easily visible
    useEffect(() => {
        if (!highlightedJob) return () => null;
        setFlashId(highlightedJob);

        const timer = setTimeout(() => setFlashId(null), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [highlightedJob]);

    return (
        <Box overflow="hidden">
            {!!selected && (
            <DialogContentSection
                title="Structures from folding jobs"
                style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                contentStyle={{ display: 'flex', flexDirection: 'column' }}
                titleIcon={(
                    <BMapsTooltip title="Refresh Jobs List">
                        <LoadingIconButton Icon={RefreshIcon} load={loadJobsList} />
                    </BMapsTooltip>
            )}
            >
                <JobsGrid
                    jobs={structureJobs}
                    userSpecifiedSelectivity={selectivityProps?.selectivity}
                    dataConnection={connection}
                    flashId={flashId}
                    localJobState={localJobState}
                    cleanup={closeImportPane}
                    retrieveJobs={retrieveJobs}
                    errors={errors}
                />
            </DialogContentSection>
            )}
        </Box>
    );
}

export function JobsGrid({
    jobs, localJobState, cleanup, flashId, errors,
    userSpecifiedSelectivity, dataConnection, preventMultiSelect,
    setActiveJobs,
}) {
    const [checkedRowItems, setCheckedRowItems] = useState([]);
    const apiRef = useGridApiRef();

    const dispatch = useDispatch();
    const setJobError = (jobid, error) => dispatch(updateLocalJobState({ jobid, error }));
    const loadJob = (jobid, options) => dispatch(loadResult(jobid, options));
    const getErrorMessage = (jobid) => dispatch(updateJobErrors(jobid));
    const deleteJobs = (jobids) => {
        for (const jobid of jobids) {
            dispatch(removeJob(jobid));
        }
    };

    // Jobs integration functions.
    async function integrateJobResults(jobids) {
        // Load jobs in the order that they appear in the table.
        const integratingJobs = [];
        for (const id of jobids) {
            const job = jobs.find((j) => j.jobid === id);
            if (job) {
                integratingJobs.push(job);
            }
        }
        if (integratingJobs.length === 0) {
            return;
        }

        // Specific logic for loading new structures
        const firstStructureJob = integratingJobs
            .find(
                (job) => jobDefinitions[job.jobtype]?.loadsMacromoleculeStructure
                && job.state === JobStatus.SUCCEEDED
            );

        if (firstStructureJob
            && userSpecifiedSelectivity === 'individual'
            && App.Workspace.needPermissionToClear()
        ) {
            if (!await okToClearAndLoad()) {
                return;
            }
        }
        const jobsWithOptions = integratingJobs.map((job) => {
            /**
             * @todo This should probably call a new JobDefinition.getIntegrateOptions, each job
             * needing to know the user specified selectivity and whether it is the first one.
             */
            let options;
            const jobDefinition = jobDefinitions[job.jobtype];
            if (jobDefinition?.loadsMacromoleculeStructure) {
                const loadOptions = {
                    // The first structure job uses the user-specified selectivity mode,
                    // the rest will be added with "multiProtein" (using the same bfd-server).
                    keepExisting: job === firstStructureJob
                        ? userSpecifiedSelectivity !== 'individual'
                        : true,
                };
                options = { dataConnection, loadOptions };
            }
            return [job, options];
        });
        // End specific logic for loading new structures

        const jobErrors = [];
        await EventBroker.withIgnoredEvents('escapeKey', (async () => {
            for (const [job, options] of jobsWithOptions) {
                const { error } = await LoadingScreen.withLoadingScreen(
                    loadJob(job.jobid, options),
                    `Integrating ${job.jobtype} job results`,
                );
                if (error) {
                    setJobError(job.jobid, error);
                    jobErrors.push(error);
                }
            }
        })()); // IIFE since withIgnoredEvents needs a promise, not a function
        if (setActiveJobs) {
            setActiveJobs(integratingJobs);
        }
        if (jobErrors.length === 0) {
            cleanup();
        }
    }

    // building the columns and rows for the data grid
    const { columns, rows } = buildJobsGrid(
        jobs, getErrorMessage, integrateJobResults, deleteJobs,
    );

    // For handling column sizing.
    // flex columns are filtered out to prevent resizing interactions.
    useEffect(() => {
        if (!rows.length) return;
        const gridResizeOptions = {
            columns: columns.filter((col) => !col.flex).map((col) => col.field),
            includeHeaders: true,
            includeOutliers: true,
        };
        apiRef.current.autosizeColumns(gridResizeOptions);
    }, [columns, apiRef, rows]);

    // Additional data grid props for using multi job intergration
    const allowMultiSelect = Loader.AllowLabFeatures && !preventMultiSelect;
    let multiLoadProps = {};
    if (allowMultiSelect) {
        const checkboxStates = [JobStatus.SUCCEEDED, JobStatus.FAILED];
        multiLoadProps = {
            checkboxSelection: true,
            isRowSelectable: (params) => checkboxStates.includes(params.row.state),
            onRowSelectionModelChange: (jobIds) => {
                setCheckedRowItems(jobIds);
            },
        };
    }

    // Message displayed by datagrid when there are no rows
    const localeText = { noRowsLabel: 'No jobs' };

    const loadButtonDisabled = jobs.filter((job) => (
        checkedRowItems.includes(job.jobid)
        && !localJobState[job.jobid]?.error
    )).length === 0;

    const removeButtonDisabled = checkedRowItems.length === 0;

    return (
        <>
            <DataGrid
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                disableColumnMenu={false}
                disableRowSelectionOnClick
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 10, page: 0 },
                    },
                }}
                pageSizeOptions={[10, 25, 50]}
                getRowClassName={(params) => (params.id === flashId ? 'flash' : '')}
                localeText={localeText}
                sx={{
                    // Header cells can be made bold with this,
                    // but it would be inconsistent with the other DataGrids, so deferring.
                    // '& .MuiDataGrid-columnHeaderTitle': {
                    //     fontWeight: 'bold',
                    // },
                    '& .flash': {
                        animation: 'flashBg 0.5s ease-in-out',
                    },
                    '@keyframes flashBg': {
                        '0%, 60%': { backgroundColor: 'lightblue' },
                        '100%': { backgroundColor: 'transparent' },
                    },
                }}
                {...multiLoadProps}
            />
            {(errors.length > 0) && <ImportErrors errors={errors} /> }
            { allowMultiSelect && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    paddingTop: '1rem',
                    width: window.screen.width > 1000 ? '14rem' : '100%',
                }}
                >
                    <BMapsTooltip title={loadButtonDisabled ? 'Select a Job to Load Results' : 'Load Selected Job Results'}>
                        {wrapForDisabledTooltip(
                            <DialogPrimaryButton
                                type="button"
                                variant="contained"
                                sx={{ width: '100%' }}
                                onClick={() => {
                                    integrateJobResults(checkedRowItems);
                                }}
                                disabled={loadButtonDisabled}
                            >
                                Load Job Results
                            </DialogPrimaryButton>
                        )}
                    </BMapsTooltip>
                    <BMapsTooltip title={removeButtonDisabled ? 'Select a Job to Remove' : 'Remove Selected Job Results'}>
                        {wrapForDisabledTooltip(
                            <DialogCancelButton
                                type="button"
                                sx={{ width: '100%' }}
                                onClick={() => {
                                    deleteJobs(checkedRowItems);
                                }}
                                disabled={removeButtonDisabled}
                            >
                                Remove Job Results
                            </DialogCancelButton>
                        )}
                    </BMapsTooltip>
                </Box>
            )}
        </>
    );
}

/**
 * Takes the job data and builds the columns and rows of the datagrid
 * @param {Job[]} jobs
 * @param {{ [string]: { [key: string]: any } }} localJobState
 */
function buildJobsGrid(jobs, getErrorMessage, integrateJobResults, deleteJobs) {
    // building the columns and rows for the data grid
    const columnNames = getColumnNames(jobs);
    const columns = getColumns(columnNames, getErrorMessage, integrateJobResults, deleteJobs);

    // places most recent job first, replace with timestamp once available.
    const rows = jobs
        .map((job) => {
            const jobRow = {};
            for (const col of expectedColumns) {
                jobRow[col.field] = col.valueGetter(job);
            }
            return ({ ...jobRow, id: job.jobid });
        })
        .sort((a, b) => {
            try {
                const sort = b.created.localeCompare(a.created);
                if (sort === 0) return b.id.localeCompare(a.id);
                return sort;
            } catch {
                return b.id.localeCompare(a.id);
            }
        });

    return { columns, rows };
}

/**
 *
 * @param {import('BMapsSrc/redux/jobs/slice').Job[]} jobs
 * @returns
 */
function getColumnNames(jobs) {
    const columnNames = ['created', 'jobtype', 'state'];
    for (const job of jobs) {
        for (const column of expectedColumns) {
            if (column.valueGetter(job) && !columnNames.includes(column.field)) {
                columnNames.push(column.field);
            }
        }
    }

    return columnNames;
}

function getColumns(columnNames, getErrorMessage, integrateJobResults, deleteJobs) {
    const columns = expectedColumns.filter((col) => columnNames.includes(col.field))
        .map((col) => {
            switch (col.field) {
                case 'actions':
                    return {
                        field: col.field,
                        headerName: col.headerName,
                        flex: 1,
                        minWidth: 75,
                        sortable: false,
                        filterable: false,
                        align: 'right',
                        headerAlign: 'right',
                        renderCell: ({ row }) => {
                            const loadActionStates = [JobStatus.SUCCEEDED];
                            const removeActionStates = [JobStatus.SUCCEEDED, JobStatus.FAILED];

                            const jobTypeName = jobDefinitions[row.jobtype].getJobTypeName();
                            let noun = `${jobTypeName} Result`;
                            if (Loader.AllowLabFeatures) {
                                noun += ` (${row.id})`;
                            }
                            const actions = [];

                            if (loadActionStates.includes(row.state)) {
                                actions.push(
                                    <BMapsTooltip title={`Load ${noun}`}>
                                        <IconButton
                                            size="small"
                                            onClick={() => integrateJobResults([row.id])}
                                        >
                                            <AddCircleOutlineIcon fontSize="inherit" />
                                        </IconButton>
                                    </BMapsTooltip>
                                );
                            }
                            if (removeActionStates.includes(row.state)) {
                                actions.push(
                                    <BMapsTooltip title={`Remove ${noun}`}>
                                        <IconButton
                                            size="small"
                                            onClick={() => deleteJobs([row.id])}
                                        >
                                            <ClearIcon fontSize="inherit" />
                                        </IconButton>
                                    </BMapsTooltip>
                                );
                            }
                            return (
                                <Box>
                                    {actions.map((a) => (
                                        // Actions are BMapsTooltips with unique titles
                                        <React.Fragment key={a.props.title}>{a}</React.Fragment>
                                    ))}
                                </Box>
                            );
                        },
                    };
                case 'error':
                    return {
                        field: col.field,
                        headerName: col.headerName,
                        description: 'errors',
                        minWidth: 150,
                        flex: 2,
                        renderCell: ({ value, row }) => {
                            if (!value) {
                                return null;
                            }
                            if (value === 'load error') {
                                let tooltip = 'Errors created while processing job. Click to display error information.';
                                if (Loader.AllowLabFeatures) {
                                    tooltip += ` (job id ${row.id})`;
                                }
                                return (
                                    <BMapsTooltip title={tooltip}>
                                        <Button
                                            onClick={() => getErrorMessage(row.id)}
                                            aria-label="Populate Error Text"
                                            variant="outlined"
                                            size="small"
                                            color="error"
                                        >
                                            Load Error
                                        </Button>
                                    </BMapsTooltip>
                                );
                            } else {
                                return (
                                    <BMapsTooltip title={value}>
                                        <span style={{
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        >
                                            {value}
                                        </span>
                                    </BMapsTooltip>
                                );
                            }
                        },
                    };
                case 'delivered':
                case 'created':
                    return {
                        field: col.field,
                        headerName: col.headerName,
                        minWidth: 120,
                        renderCell: ({ value, row }) => {
                            if (!value) return null;
                            const displayValue = new Date(value).toLocaleString('en-US', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            });
                            let tooltip = displayValue;
                            if (Loader.AllowLabFeatures) {
                                tooltip += ` (job id ${row.id})`;
                            }
                            return (
                                <BMapsTooltip title={tooltip}>
                                    <span style={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                    >
                                        {displayValue}
                                    </span>
                                </BMapsTooltip>
                            );
                        },
                    };
                default:
                    return { field: col.field, headerName: col.headerName };
            }
        });
    return columns;
}

const expectedColumns = [
    {
        field: 'moleculeName',
        headerName: 'Name',
        valueGetter: (job) => job.requestmeta?.moleculeName,
    },
    {
        field: 'created',
        headerName: 'Start Time',
        valueGetter: (job) => job.created,
    },
    {
        field: 'jobtype',
        headerName: 'Job Type',
        valueGetter: (job) => job.jobtype,
    },
    {
        field: 'foldingProgram',
        headerName: 'Program',
        valueGetter: (job) => job.requestmeta?.foldingProgram,
    },
    {
        field: 'state',
        headerName: 'State',
        valueGetter: (job) => job.state,
    },
    {
        field: 'delivered',
        headerName: 'Last Viewed',
        valueGetter: (job) => job.delivered,
    },
    {
        field: 'error',
        headerName: 'Errors',
        valueGetter: (job) => {
            const { localJobState } = getJobState();
            if (localJobState[job.jobid]?.error) {
                return localJobState[job.jobid].error;
            }
            if (job.state === 'failed') {
                return 'load error';
            }
            return null;
        },
    },
    {
        field: 'actions',
        headerName: ' ',
        valueGetter: () => true,
    },

];
